import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import type { InspectionOrder, InspectionOrderDevice, InspectionOrderDeviceSummary, NfcTagLookupResponse, TechnicianInspectionOrderDeviceListResponse } from '../types/inspectionOrder';
import { normalizeInspectionOrder, normalizeInspectionOrderDevice, type RawInspectionOrder, type RawInspectionOrderDevice } from '../utils/normalizeInspectionOrder';
import { Tabs } from '../components/ui/Tabs';
import { useInspectionOrdersStore } from '../store/inspectionOrdersStore';
import { NfcScanButton } from '../components/nfc/NfcScanButton';
import './InspectionDetailPage.css';

type DetailState = {
  order: InspectionOrder | null;
  isLoading: boolean;
  error: string | null;
};

const formatScheduledAt = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const InspectionDetailPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const updateOrderInStore = useInspectionOrdersStore((store) => store.updateOrder);
  const [state, setState] = useState<DetailState>({
    order: null,
    isLoading: true,
    error: null
  });
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startSuccess, setStartSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'order' | 'devices' | 'more'>('order');
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');
  const [deviceSearchResults, setDeviceSearchResults] = useState<InspectionOrderDevice[]>([]);
  const [isSearchingDevices, setIsSearchingDevices] = useState(false);
  const [deviceSearchError, setDeviceSearchError] = useState<string | null>(null);
  const [hasSearchedDevices, setHasSearchedDevices] = useState(false);
  const [deviceSummary, setDeviceSummary] = useState<InspectionOrderDeviceSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [nfcMessage, setNfcMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [loadedDevices, setLoadedDevices] = useState<InspectionOrderDevice[]>([]);

  const loadOrder = useCallback(async (token?: { cancelled: boolean }) => {
    const safeSetState = (updater: (previous: DetailState) => DetailState) => {
      if (!token?.cancelled) {
        setState(updater);
      }
    };
    const safeResetActionState = () => {
      if (!token?.cancelled) {
        setStartError(null);
        setStartSuccess(null);
      }
    };

    if (!orderId) {
      safeResetActionState();
      safeSetState(() => ({
        order: null,
        isLoading: false,
        error: 'Auftrags-ID fehlt. Bitte zur Übersicht zurückkehren.'
      }));
      return;
    }

    safeSetState((previous) => ({
      ...previous,
      isLoading: true,
      error: null
    }));
    safeResetActionState();

    try {
      const response = await api.get<RawInspectionOrder>(`technicians/me/inspection-orders/${orderId}`);
      const normalized = normalizeInspectionOrder(response as unknown as RawInspectionOrder);

      if (!normalized) {
        safeSetState(() => ({
          order: null,
          isLoading: false,
          error: 'Auftrag konnte nicht geladen werden. Bitte erneut versuchen.'
        }));
        return;
      }

      safeSetState(() => ({
        order: normalized,
        isLoading: false,
        error: null
      }));
    } catch (fetchError) {
      console.error('inspectionDetail:load', fetchError);
      safeSetState(() => ({
        order: null,
        isLoading: false,
        error: 'Auftrag konnte nicht geladen werden. Bitte erneut versuchen.'
      }));
    }
  }, [orderId]);

  const loadDeviceSummary = useCallback(async (targetOrderId: string, token?: { cancelled: boolean }) => {
    if (!targetOrderId) {
      return;
    }

    if (!token?.cancelled) {
      setIsLoadingSummary(true);
    }

    try {
      const response = await api.get<InspectionOrderDeviceSummary>(
        `technicians/me/inspection-orders/${targetOrderId}/devices/summary`
      );
      if (!token?.cancelled) {
        setDeviceSummary(response as unknown as InspectionOrderDeviceSummary);
      }
    } catch (summaryError) {
      console.error('inspectionDetail:loadSummary', summaryError);
      if (!token?.cancelled) {
        setDeviceSummary(null);
      }
    } finally {
      if (!token?.cancelled) {
        setIsLoadingSummary(false);
      }
    }
  }, []);

  useEffect(() => {
    const state = { cancelled: false };
    void loadOrder(state);
    return () => {
      state.cancelled = true;
    };
  }, [loadOrder]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    const state = { cancelled: false };
    void loadDeviceSummary(orderId, state);
    return () => {
      state.cancelled = true;
    };
  }, [orderId, loadDeviceSummary, location.key]);

  const { order, isLoading, error } = state;

  useEffect(() => {
    setDeviceSearchTerm('');
    setDeviceSearchResults([]);
    setDeviceSearchError(null);
    setHasSearchedDevices(false);
    setIsSearchingDevices(false);
  }, [order?.id]);

  const tabs = useMemo(
    () => [
      { id: 'order', label: 'Auftrag' },
      { id: 'devices', label: 'Geräte' },
      { id: 'more', label: 'Weitere Infos (bald verfügbar)', disabled: true }
    ],
    []
  );
  const scheduledLabel = useMemo(() => formatScheduledAt(order?.scheduledStart), [order?.scheduledStart]);
  const orderReference = useMemo(() => order?.id ?? orderId ?? null, [order?.id, orderId]);
  const stateLabel = useMemo(() => {
    if (!order) {
      return null;
    }
    if (order.stateLabel && order.stateLabel.trim().length > 0) {
      return order.stateLabel.trim();
    }
    if (order.state && order.state.trim().length > 0) {
      return order.state.trim();
    }
    return null;
  }, [order]);
  const isOpenOrder = useMemo(() => {
    if (!order) {
      return false;
    }
    const normalizedState = order.state?.trim().toUpperCase();
    if (normalizedState === 'OPEN') {
      return true;
    }
    const normalizedLabel = order.stateLabel?.trim().toLowerCase();
    return normalizedLabel === 'offen';
  }, [order]);
  const canStart = !isLoading && !error && isOpenOrder;
  const hasActionFeedback = startError !== null || startSuccess !== null;
  const valueOrPlaceholder = useCallback(
    (value: string | null | undefined, fallback = 'Keine Angabe') =>
      typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback,
    []
  );
  const normalizeSearchDevices = useCallback(
    (response: TechnicianInspectionOrderDeviceListResponse | null | undefined) => {
      if (!response || !Array.isArray(response.devices)) {
        return [];
      }

      return response.devices
        .map((device) => normalizeInspectionOrderDevice(device as RawInspectionOrderDevice))
        .filter((device): device is InspectionOrderDevice => device !== null);
    },
    []
  );
  const handleTabSelect = useCallback(
    (tabId: string) => {
      if (tabId !== 'order' && tabId !== 'devices') {
        return;
      }
      setActiveTab(tabId);

      const params = new URLSearchParams(location.search);
      if (tabId === 'devices') {
        params.set('tab', 'devices');
      } else {
        params.delete('tab');
      }

      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab') === 'devices' ? 'devices' : 'order';
    setActiveTab((previous) => (previous === requestedTab ? previous : requestedTab));
  }, [location.search]);

  const handleRetry = useCallback(() => {
    setStartError(null);
    setStartSuccess(null);
    void loadOrder();
  }, [loadOrder]);

  const handleSearchTermChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setDeviceSearchTerm(nextValue);
    if (nextValue.trim().length === 0) {
      setHasSearchedDevices(false);
      setDeviceSearchResults([]);
      setDeviceSearchError(null);
    }
  }, []);

  const handleDeviceSearch = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (!order?.id) {
        return;
      }

      const term = deviceSearchTerm.trim();
      setDeviceSearchError(null);

      if (term.length === 0) {
        setHasSearchedDevices(false);
        setDeviceSearchResults([]);
        return;
      }

      setHasSearchedDevices(true);
      setIsSearchingDevices(true);

      try {
        const response = await api.get<TechnicianInspectionOrderDeviceListResponse>(
          `technicians/me/inspection-orders/${order.id}/devices`,
          {
            params: {
              filter: term,
              limit: 25,
              offset: 0
            }
          }
        );
        const normalizedResponse = response as unknown as TechnicianInspectionOrderDeviceListResponse;
        const devices = normalizeSearchDevices(normalizedResponse);
        setDeviceSearchResults(devices);
        setLoadedDevices((prev) => {
          const combined = [...prev];
          for (const device of devices) {
            if (!combined.some((d) => d.id === device.id)) {
              combined.push(device);
            }
          }
          return combined;
        });
      } catch (searchError) {
        console.error('inspectionDetail:deviceSearch', searchError);
        setDeviceSearchError('Geräte konnten nicht geladen werden. Bitte erneut suchen.');
        setDeviceSearchResults([]);
      } finally {
        setIsSearchingDevices(false);
      }
    },
    [deviceSearchTerm, normalizeSearchDevices, order?.id]
  );

  const handleStart = useCallback(async () => {
    if (!order?.id) {
      return;
    }

    setStartError(null);
    setStartSuccess(null);
    setIsStarting(true);

    try {
      const response = await api.post<RawInspectionOrder>(`technicians/me/inspection-orders/${order.id}/start`);
      const normalized = normalizeInspectionOrder(response as unknown as RawInspectionOrder);

      if (!normalized) {
        throw new Error('invalid-response');
      }

      setState((previous) => ({
        ...previous,
        order: normalized
      }));
      updateOrderInStore(normalized);
      setStartSuccess('Auftrag wurde gestartet.');
    } catch (startErr) {
      console.error('inspectionDetail:start', startErr);
      setStartError('Auftrag konnte nicht gestartet werden. Bitte erneut versuchen.');
    } finally {
      setIsStarting(false);
    }
  }, [order, updateOrderInStore]);

  const handleNavigateToDevice = useCallback(
    (device: InspectionOrderDevice, index?: number) => {
      if (!device || !order?.id) {
        return;
      }

      const knownDevices = loadedDevices;
      const resolvedIndex =
        typeof index === 'number'
          ? index
          : knownDevices.findIndex((candidate) => {
              const candidateId = candidate.id?.trim();
              const candidateIdentifier = candidate.identifier?.trim();
              const deviceId = device.id?.trim();
              const deviceIdentifier = device.identifier?.trim();
              return (
                (deviceId && candidateId && candidateId === deviceId) ||
                (deviceIdentifier &&
                  candidateIdentifier &&
                  candidateIdentifier.toLowerCase() === deviceIdentifier.toLowerCase())
              );
            });

      const deviceRouteId =
        device.id && device.id.trim().length > 0
          ? device.id
          : device.identifier && device.identifier.trim().length > 0
            ? device.identifier
            : String(typeof index === 'number' ? index : resolvedIndex >= 0 ? resolvedIndex : 0);

      navigate(`/inspections/${order.id}/devices/${encodeURIComponent(deviceRouteId)}`, {
        state: {
          from: `${location.pathname}${location.search}`,
          deviceLookup: {
            id: device.id ?? null,
            identifier: device.identifier ?? null,
            index: resolvedIndex >= 0 ? resolvedIndex : index
          }
        }
      });
    },
    [location.pathname, location.search, navigate, order?.id, loadedDevices]
  );

  const handleNfcDeviceFound = useCallback(
    (result: NfcTagLookupResponse) => {
      setNfcMessage(null);
      if (result.orderDevice) {
        const normalized = normalizeInspectionOrderDevice(result.orderDevice as unknown as RawInspectionOrderDevice);
        if (normalized) {
          handleNavigateToDevice(normalized);
        }
      }
    },
    [handleNavigateToDevice]
  );

  const handleNfcDeviceNotInOrder = useCallback(
    (result: NfcTagLookupResponse) => {
      if (result.deviceInfo) {
        setNfcMessage({
          type: 'warning',
          text: `Gerät "${result.deviceInfo.identifier}" (${result.deviceInfo.equipmentTypeName}) ist nicht Teil dieses Prüfauftrags.`
        });
      }
    },
    []
  );

  const handleNfcDeviceNotFound = useCallback(() => {
    setNfcMessage({
      type: 'error',
      text: 'Unbekannter NFC-Tag'
    });
  }, []);

  return (
    <section className="inspection-detail-page">
      <header className="inspection-detail-page__header">
        <h2 id="inspection-detail-page-title">Inspektionsdetails</h2>
        {orderReference && (
          <p className="inspection-detail-page__meta inspection-detail-page__meta--muted">
            Auftragsnummer: <strong>{orderReference}</strong>
          </p>
        )}
      </header>

      <Tabs
        tabs={tabs}
        activeTabId={activeTab}
        onTabSelect={handleTabSelect}
        tablistLabel="Details zum Inspektionsauftrag"
        ariaLabelledBy="inspection-detail-page-title"
      />

      <div
        role="tabpanel"
        id="tabpanel-order"
        aria-labelledby="tab-order"
        className="inspection-detail-page__tabpanel"
        hidden={activeTab !== 'order'}
      >
        <div className="inspection-detail-page__tabpanel-content">
          {isLoading && (
            <div className="inspection-detail-page__state inspection-detail-page__state--loading" role="status">
              <span className="inspection-detail-page__spinner" aria-hidden="true" />
              <span>Auftrag wird geladen ...</span>
            </div>
          )}

          {!isLoading && error && (
            <div className="inspection-detail-page__state inspection-detail-page__state--error" role="alert">
              <p>{error}</p>
              <button type="button" className="inspection-detail-page__retry" onClick={handleRetry}>
                Erneut versuchen
              </button>
            </div>
          )}

          {!isLoading && !error && order && (
            <div className="inspection-detail-page__order-content">
              <section className="inspection-detail-page__info-card">
                <div className="inspection-detail-page__info-card-header">
                  <h3>Auftragsdaten</h3>
                  {canStart && (
                    <button
                      type="button"
                      className="inspection-detail-page__start-button"
                      onClick={handleStart}
                      disabled={isStarting}
                    >
                      {isStarting ? 'Wird gestartet...' : 'Auftrag starten'}
                    </button>
                  )}
                </div>

                {hasActionFeedback && (
                  <div className="inspection-detail-page__action-feedback" aria-live="polite">
                    {startSuccess && (
                      <div className="inspection-detail-page__alert inspection-detail-page__alert--success" role="status">
                        {startSuccess}
                      </div>
                    )}
                    {startError && (
                      <div className="inspection-detail-page__alert inspection-detail-page__alert--error" role="alert">
                        {startError}
                      </div>
                    )}
                  </div>
                )}

                <dl className="inspection-detail-page__info-grid">
                  <div className="inspection-detail-page__info-item">
                    <dt>Status</dt>
                    <dd>{valueOrPlaceholder(stateLabel, 'Keine Angabe')}</dd>
                  </div>
                  <div className="inspection-detail-page__info-item">
                    <dt>Geplanter Start</dt>
                    <dd>{valueOrPlaceholder(scheduledLabel, 'Keine Angabe')}</dd>
                  </div>
                  <div className="inspection-detail-page__info-item">
                    <dt>Kunde</dt>
                    <dd>
                      <div className="inspection-detail-page__info-value">
                        <span className="inspection-detail-page__info-primary">
                          {valueOrPlaceholder(order.customerName, 'Keine Angabe')}
                        </span>
                        <span className="inspection-detail-page__info-secondary">
                          {valueOrPlaceholder(order.customerAddress)}
                        </span>
                      </div>
                    </dd>
                  </div>
                  <div className="inspection-detail-page__info-item">
                    <dt>Standort</dt>
                    <dd>
                      <div className="inspection-detail-page__info-value">
                        <span className="inspection-detail-page__info-primary">
                          {valueOrPlaceholder(order.siteName, 'Keine Angabe')}
                        </span>
                        <span className="inspection-detail-page__info-secondary">
                          {valueOrPlaceholder(order.siteAddress)}
                        </span>
                      </div>
                    </dd>
                  </div>
                  <div className="inspection-detail-page__info-item">
                    <dt>Prüffortschritt</dt>
                    <dd>
                      {isLoadingSummary && (
                        <span className="inspection-detail-page__progress-loading">Wird geladen...</span>
                      )}
                      {!isLoadingSummary && deviceSummary && (
                        <div className="inspection-detail-page__progress-counter">
                          <span className="inspection-detail-page__progress-value">
                            {deviceSummary.completed}/{deviceSummary.total}
                          </span>
                          <span className="inspection-detail-page__progress-label">Geräte abgeschlossen</span>
                        </div>
                      )}
                      {!isLoadingSummary && !deviceSummary && (
                        <span>Keine Angabe</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </section>
            </div>
          )}

          {!isLoading && !error && !order && (
            <div className="inspection-detail-page__state inspection-detail-page__state--empty">
              <p>Keine Auftragsdaten verfügbar.</p>
            </div>
          )}
        </div>
      </div>

      <div
        role="tabpanel"
        id="tabpanel-devices"
        aria-labelledby="tab-devices"
        className="inspection-detail-page__tabpanel"
        hidden={activeTab !== 'devices'}
      >
        <div className="inspection-detail-page__tabpanel-content">
          {isLoading && (
            <div className="inspection-detail-page__state inspection-detail-page__state--loading" role="status">
              <span className="inspection-detail-page__spinner" aria-hidden="true" />
              <span>Geräte werden geladen ...</span>
            </div>
          )}

          {!isLoading && error && (
            <div className="inspection-detail-page__state inspection-detail-page__state--error" role="alert">
              <p>{error}</p>
              <button type="button" className="inspection-detail-page__retry" onClick={handleRetry}>
                Erneut versuchen
              </button>
            </div>
          )}

          {!isLoading && !error && !order && (
            <div className="inspection-detail-page__state inspection-detail-page__state--empty">
              <p>Keine Auftragsdaten verfügbar.</p>
            </div>
          )}

          {!isLoading && !error && order && (
            <div className="inspection-detail-page__device-search">
              <div className="inspection-detail-page__nfc-section">
                <NfcScanButton
                  orderId={order.id}
                  onDeviceFound={handleNfcDeviceFound}
                  onNotInOrder={handleNfcDeviceNotInOrder}
                  onNotFound={handleNfcDeviceNotFound}
                />
                {nfcMessage && (
                  <div
                    className={`inspection-detail-page__nfc-message inspection-detail-page__nfc-message--${nfcMessage.type}`}
                    role={nfcMessage.type === 'error' ? 'alert' : 'status'}
                  >
                    {nfcMessage.text}
                  </div>
                )}
              </div>

              <div className="inspection-detail-page__search-divider">
                <span>oder</span>
              </div>

              <form className="inspection-detail-page__device-search-form" onSubmit={handleDeviceSearch}>
                <label className="inspection-detail-page__device-search-label" htmlFor="device-search-input">
                  Gerät nach Kennung suchen
                </label>
                <div className="inspection-detail-page__device-search-controls">
                  <input
                    id="device-search-input"
                    name="device-search"
                    type="search"
                    value={deviceSearchTerm}
                    onChange={handleSearchTermChange}
                    placeholder="Kennung eingeben"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="inspection-detail-page__device-search-button"
                    disabled={isSearchingDevices || deviceSearchTerm.trim().length === 0}
                  >
                    {isSearchingDevices ? 'Suche läuft ...' : 'Suchen'}
                  </button>
                </div>
              </form>

              {deviceSearchError && !isSearchingDevices && (
                <div className="inspection-detail-page__state inspection-detail-page__state--error" role="alert">
                  <p>{deviceSearchError}</p>
                </div>
              )}

              {!hasSearchedDevices && !deviceSearchError && !isSearchingDevices && (
                <div className="inspection-detail-page__state inspection-detail-page__state--hint">
                  <p>Bitte Suchbegriff eingeben, um Geräte zu laden</p>
                </div>
              )}

              {isSearchingDevices && (
                <div className="inspection-detail-page__state inspection-detail-page__state--loading" role="status">
                  <span className="inspection-detail-page__spinner" aria-hidden="true" />
                  <span>Geräte werden geladen ...</span>
                </div>
              )}

              {hasSearchedDevices && !isSearchingDevices && !deviceSearchError && (
                <>
                  {(deviceSearchResults?.length ?? 0) === 0 ? (
                    <div className="inspection-detail-page__state inspection-detail-page__state--empty">
                      <p>Keine Geräte gefunden</p>
                    </div>
                  ) : (
                    <div className="inspection-detail-page__device-list">
                      {(deviceSearchResults ?? []).map((device, index) => {
                        const identifierLabel = valueOrPlaceholder(device.identifier, 'Keine Kennung angegeben');
                        const equipmentTypeLabel = valueOrPlaceholder(device.equipmentType, 'Gerätetyp unbekannt');
                        const panelTitleId = `device-card-${index}`;
                        const key = device.id ?? `${identifierLabel}-${index}`;
                        const knownIndex =
                          loadedDevices.findIndex(
                            (candidate) =>
                              (device.id && candidate.id === device.id) ||
                              (device.identifier &&
                                candidate.identifier &&
                                candidate.identifier.toLowerCase() === device.identifier.toLowerCase())
                          );
                        const handleSelect = () => handleNavigateToDevice(device, knownIndex >= 0 ? knownIndex : index);
                        const normalizedStatus = device.status?.trim().toUpperCase();
                        const normalizedStatusLabel = device.statusLabel?.trim().toLowerCase();
                        const isCompleted = normalizedStatus === 'COMPLETED' || normalizedStatusLabel === 'abgeschlossen';
                        const statusClass = isCompleted
                          ? 'inspection-detail-page__device-status--completed'
                          : 'inspection-detail-page__device-status--open';
                        const statusLabel = device.statusLabel ?? (isCompleted ? 'Abgeschlossen' : 'Offen');

                        return (
                          <button
                            key={key}
                            type="button"
                            className="inspection-detail-page__device-card"
                            aria-labelledby={panelTitleId}
                            onClick={handleSelect}
                          >
                            <header className="inspection-detail-page__device-card-header">
                              <h3 id={panelTitleId}>{identifierLabel}</h3>
                              <span
                                className={`inspection-detail-page__device-status ${statusClass}`}
                                aria-label={`Status: ${statusLabel}`}
                              >
                                {statusLabel}
                              </span>
                            </header>
                            <p className="inspection-detail-page__device-meta">
                              Gerätetyp: <span>{equipmentTypeLabel}</span>
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
